/**
 * TanStack Query hooks for Referral data
 *
 * These hooks replace the manual useState + apiClient calls in ReferFriendsPage,
 * providing automatic caching, refetchOnWindowFocus, and easy invalidation.
 *
 * Key behavior:
 * - Referral counts and invites refetch on window focus so the page updates
 *   when a referred user registers while the referrer has the tab backgrounded.
 * - Leaderboard data uses a longer staleTime since it changes less frequently.
 * - Mutations (createInvite, sendReminder, etc.) invalidate related queries.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Constants ───────────────────────────────────────────────────────────────

const TWO_MINUTES = 2 * 60 * 1000;
const FIVE_MINUTES = 5 * 60 * 1000;
const TEN_MINUTES = 10 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ReferralCounts {
  totalReferrals: number;
  qualifiedReferrals: number;
  totalGcEarned: number;
  totalScEarned: number;
  claimableTierCount: number;
  claimableTierGc: number;
  claimableTierSc: number;
}

export interface ReferralInvite {
  id: number;
  invite_email: string;
  status: string;
  created_at: string;
  last_reminder_sent_at: string | null;
}

export interface ReferralInvitesResponse {
  invites: ReferralInvite[];
  totalPages: number;
  total: number;
  remindableCount: number;
}

export interface ReferralTier {
  tier_level: number;
  tier_name: string;
  state: string; // 'claimed' | 'achieved' | 'next' | 'locked'
}

export interface ReferralProfile {
  first_name?: string;
  last_name?: string;
  username?: string;
  email?: string;
}

export interface QualifiedReferral {
  id: number;
  profiles?: ReferralProfile;
  pool_qualified_at: string | null;
  tiers: ReferralTier[];
}

export interface ReferralStatsResponse {
  referrals: QualifiedReferral[];
  totalPages: number;
  totalReferrals: number;
}

export interface BonusTier {
  id: number;
  tier_level: number;
  tier_name: string;
  gc_reward_amount: number;
  sc_reward_amount: number;
  minimum_purchase_amount: number;
  is_enabled: boolean;
}

export interface PoolTier {
  id: number;
  tier_name: string;
  gc_reward_amount: number;
  sc_reward_amount: number;
}

export interface PoolLeaderboardEntry {
  rank: number;
  masked_username: string;
  qualified_referrals: number;
  is_current_user: boolean;
}

export interface MyRank {
  rank: number | null;
  qualified_referrals: number;
  in_top_10: boolean;
}

export interface PreviousMonthEntry {
  rank: number;
  masked_username: string;
  qualified_referrals: number;
  gc_reward_amount: number;
  sc_reward_amount: number;
  is_current_user: boolean;
  can_claim: boolean;
}

export interface PreviousMonthData {
  year: number;
  month: number;
  entries: PreviousMonthEntry[];
}

// ─── Query Key Factory ───────────────────────────────────────────────────────

export const referralKeys = {
  all: ['referral'] as const,
  code: () => [...referralKeys.all, 'code'] as const,
  counts: () => [...referralKeys.all, 'counts'] as const,
  invites: (page: number, limit: number) => [...referralKeys.all, 'invites', { page, limit }] as const,
  stats: (page: number, limit: number) => [...referralKeys.all, 'stats', { page, limit }] as const,
  bonusTiers: () => [...referralKeys.all, 'bonusTiers'] as const,
  maxScReward: () => [...referralKeys.all, 'maxScReward'] as const,
  summary: () => [...referralKeys.all, 'summary'] as const,
  pageInit: () => [...referralKeys.all, 'pageInit'] as const,
  poolStatus: () => [...referralKeys.all, 'pool', 'status'] as const,
  poolLeaderboard: () => [...referralKeys.all, 'pool', 'leaderboard'] as const,
  myRank: () => [...referralKeys.all, 'pool', 'myRank'] as const,
  previousMonth: () => [...referralKeys.all, 'pool', 'previousMonth'] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Fetch the user's referral code.
 * Rarely changes — long staleTime.
 */
export function useReferralCodeQuery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: referralKeys.code(),
    queryFn: async (): Promise<string | null> => {
      const data = await apiClient.get<{ success: boolean; referralCode: string }>('/referral/code');
      return data?.referralCode ?? null;
    },
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES * 2,
    enabled: !!user,
  });
}

/**
 * Fetch referral counts (invited, qualified, GC earned, SC earned).
 * Refetches on window focus so the counts update when someone registers.
 */
export function useReferralCountsQuery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: referralKeys.counts(),
    queryFn: async (): Promise<ReferralCounts> => {
      const data = await apiClient.get<{
        success: boolean;
        totalReferrals: number;
        qualifiedReferrals: number;
        totalGcEarned: number;
        totalScEarned: number;
        claimableTierCount: number;
        claimableTierGc: number;
        claimableTierSc: number;
      }>('/referral/counts');
      return {
        totalReferrals: data?.totalReferrals ?? 0,
        qualifiedReferrals: data?.qualifiedReferrals ?? 0,
        totalGcEarned: data?.totalGcEarned ?? 0,
        totalScEarned: data?.totalScEarned ?? 0,
        claimableTierCount: data?.claimableTierCount ?? 0,
        claimableTierGc: data?.claimableTierGc ?? 0,
        claimableTierSc: data?.claimableTierSc ?? 0,
      };
    },
    staleTime: TWO_MINUTES,
    gcTime: TEN_MINUTES,
    refetchOnWindowFocus: true,
    enabled: !!user,
  });
}

/**
 * Fetch paginated invites list.
 * Refetches on window focus so new invite acceptances show up.
 */
export function useReferralInvitesQuery(page: number, limit: number) {
  const { user } = useAuth();

  return useQuery({
    queryKey: referralKeys.invites(page, limit),
    queryFn: async (): Promise<ReferralInvitesResponse> => {
      const data = await apiClient.get<{
        success: boolean;
        invites: ReferralInvite[];
        totalPages: number;
        total: number;
        remindableCount: number;
      }>(`/referral/invites?page=${page}&limit=${limit}`);
      return {
        invites: data?.invites ?? [],
        totalPages: data?.totalPages ?? 1,
        total: data?.total ?? 0,
        remindableCount: data?.remindableCount ?? 0,
      };
    },
    staleTime: TWO_MINUTES,
    gcTime: TEN_MINUTES,
    refetchOnWindowFocus: true,
    enabled: !!user,
  });
}

/**
 * Fetch paginated qualified referrals.
 * Refetches on window focus.
 */
export function useReferralStatsQuery(page: number, limit: number) {
  const { user } = useAuth();

  return useQuery({
    queryKey: referralKeys.stats(page, limit),
    queryFn: async (): Promise<ReferralStatsResponse> => {
      const data = await apiClient.get<{
        success: boolean;
        referrals: QualifiedReferral[];
        totalPages: number;
        totalReferrals: number;
      }>(`/referral/stats?page=${page}&limit=${limit}`);
      return {
        referrals: data?.referrals ?? [],
        totalPages: data?.totalPages ?? 1,
        totalReferrals: data?.totalReferrals ?? 0,
      };
    },
    staleTime: TWO_MINUTES,
    gcTime: TEN_MINUTES,
    refetchOnWindowFocus: true,
    enabled: !!user,
  });
}

/**
 * Fetch enabled bonus tiers.
 * Rarely changes — longer staleTime.
 */
export function useReferralBonusTiersQuery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: referralKeys.bonusTiers(),
    queryFn: async (): Promise<BonusTier[]> => {
      const data = await apiClient.get<{ success: boolean; tiers: BonusTier[] }>('/referral/bonus-tiers');
      return data?.tiers ?? [];
    },
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    enabled: !!user,
  });
}

/**
 * Fetch max SC reward amount.
 * Rarely changes — longer staleTime.
 */
export function useReferralMaxScRewardQuery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: referralKeys.maxScReward(),
    queryFn: async (): Promise<number> => {
      const data = await apiClient.get<{ success: boolean; maxScReward: number }>('/referral/max-sc-reward');
      return Number(data?.maxScReward) || 0;
    },
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    enabled: !!user,
  });
}

export interface ReferralSummary {
  referralCode: string | null;
  bonusTiers: BonusTier[];
  maxGcReward: number;
  maxScReward: number;
}

/**
 * Fetch referral summary: code + bonus tiers + max rewards in a single call.
 * Use this instead of separate useReferralCodeQuery + useReferralBonusTiersQuery + useReferralMaxScRewardQuery.
 */
export function useReferralSummaryQuery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: referralKeys.summary(),
    queryFn: async (): Promise<ReferralSummary> => {
      const data = await apiClient.get<{ success: boolean } & ReferralSummary>('/referral/summary');
      return {
        referralCode: data?.referralCode ?? null,
        bonusTiers: data?.bonusTiers ?? [],
        maxGcReward: Number(data?.maxGcReward) || 0,
        maxScReward: Number(data?.maxScReward) || 0,
      };
    },
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    enabled: !!user,
  });
}

// ─── Page Init (Consolidated) ─────────────────────────────────────────────

export interface ReferralPageInitData {
  summary: ReferralSummary;
  counts: ReferralCounts;
  invites: ReferralInvitesResponse;
  stats: ReferralStatsResponse;
}

function parsePageInitResponse(data: any): ReferralPageInitData {
  return {
    summary: {
      referralCode: data?.summary?.referralCode ?? null,
      bonusTiers: data?.summary?.bonusTiers ?? [],
      maxGcReward: Number(data?.summary?.maxGcReward) || 0,
      maxScReward: Number(data?.summary?.maxScReward) || 0,
    },
    counts: {
      totalReferrals: data?.counts?.totalReferrals ?? 0,
      qualifiedReferrals: data?.counts?.qualifiedReferrals ?? 0,
      totalGcEarned: data?.counts?.totalGcEarned ?? 0,
      totalScEarned: data?.counts?.totalScEarned ?? 0,
      claimableTierCount: data?.counts?.claimableTierCount ?? 0,
      claimableTierGc: data?.counts?.claimableTierGc ?? 0,
      claimableTierSc: data?.counts?.claimableTierSc ?? 0,
    },
    invites: {
      invites: data?.invites?.invites ?? [],
      totalPages: data?.invites?.totalPages ?? 1,
      total: data?.invites?.total ?? 0,
      remindableCount: data?.invites?.remindableCount ?? 0,
    },
    stats: {
      referrals: data?.stats?.referrals ?? [],
      totalPages: data?.stats?.totalPages ?? 1,
      totalReferrals: data?.stats?.totalReferrals ?? 0,
    },
  };
}

/**
 * Core data hook for the Refer Friends page.
 *
 * Uses a single /referral/page-init call for summary, counts, invites, and stats.
 * Individual invites/stats queries only fire for pagination (page 2+).
 * Pool data (leaderboard, rank, etc.) is fetched separately via pool hooks.
 */
export function useReferralPageData(invitesPage: number, invitesLimit: number, referralsPage: number, referralsLimit: number) {
  const { user } = useAuth();

  const { data: pageInit, isLoading: pageInitLoading } = useQuery({
    queryKey: referralKeys.pageInit(),
    queryFn: async (): Promise<ReferralPageInitData> => {
      const data = await apiClient.get<{ success: boolean } & ReferralPageInitData>('/referral/page-init');
      return parsePageInitResponse(data);
    },
    staleTime: TWO_MINUTES,
    gcTime: TEN_MINUTES,
    refetchOnWindowFocus: true,
    enabled: !!user,
  });

  // Individual paginated queries — only fire for page 2+ (page 1 comes from page-init)
  const { data: invitesPaginated, isLoading: invitesPaginatedLoading } = useQuery({
    queryKey: referralKeys.invites(invitesPage, invitesLimit),
    queryFn: async (): Promise<ReferralInvitesResponse> => {
      const data = await apiClient.get<{
        success: boolean;
        invites: ReferralInvite[];
        totalPages: number;
        total: number;
        remindableCount: number;
      }>(`/referral/invites?page=${invitesPage}&limit=${invitesLimit}`);
      return {
        invites: data?.invites ?? [],
        totalPages: data?.totalPages ?? 1,
        total: data?.total ?? 0,
        remindableCount: data?.remindableCount ?? 0,
      };
    },
    staleTime: TWO_MINUTES,
    gcTime: TEN_MINUTES,
    refetchOnWindowFocus: true,
    enabled: !!user && invitesPage > 1,
  });

  const { data: statsPaginated, isLoading: statsPaginatedLoading } = useQuery({
    queryKey: referralKeys.stats(referralsPage, referralsLimit),
    queryFn: async (): Promise<ReferralStatsResponse> => {
      const data = await apiClient.get<{
        success: boolean;
        referrals: QualifiedReferral[];
        totalPages: number;
        totalReferrals: number;
      }>(`/referral/stats?page=${referralsPage}&limit=${referralsLimit}`);
      return {
        referrals: data?.referrals ?? [],
        totalPages: data?.totalPages ?? 1,
        totalReferrals: data?.totalReferrals ?? 0,
      };
    },
    staleTime: TWO_MINUTES,
    gcTime: TEN_MINUTES,
    refetchOnWindowFocus: true,
    enabled: !!user && referralsPage > 1,
  });

  return {
    summary: pageInit?.summary,
    counts: pageInit?.counts,
    invitesData: invitesPage > 1 ? invitesPaginated : pageInit?.invites,
    statsData: referralsPage > 1 ? statsPaginated : pageInit?.stats,
    summaryLoading: pageInitLoading,
    countsLoading: pageInitLoading,
    invitesLoading: invitesPage > 1 ? invitesPaginatedLoading : pageInitLoading,
    statsLoading: referralsPage > 1 ? statsPaginatedLoading : pageInitLoading,
  };
}

/**
 * Fetch pool feature status and tiers.
 * Rarely changes.
 */
export function useReferralPoolStatusQuery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: referralKeys.poolStatus(),
    queryFn: async (): Promise<{ enabled: boolean; tiers: PoolTier[] }> => {
      const data = await apiClient.get<{ success: boolean; enabled: boolean; tiers: PoolTier[] }>('/referral/pool/status');
      return {
        enabled: data?.enabled ?? false,
        tiers: data?.tiers ?? [],
      };
    },
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    enabled: !!user,
  });
}

/**
 * Fetch pool leaderboard.
 * Refetches on window focus with a moderate staleTime.
 */
export function useReferralPoolLeaderboardQuery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: referralKeys.poolLeaderboard(),
    queryFn: async (): Promise<PoolLeaderboardEntry[]> => {
      const data = await apiClient.get<{ success: boolean; leaderboard: PoolLeaderboardEntry[] }>('/referral/pool/leaderboard');
      return data?.leaderboard ?? [];
    },
    staleTime: FIVE_MINUTES,
    gcTime: TEN_MINUTES,
    refetchOnWindowFocus: true,
    enabled: !!user,
  });
}

/**
 * Fetch user's rank in the pool.
 * Refetches on window focus.
 */
export function useReferralMyRankQuery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: referralKeys.myRank(),
    queryFn: async (): Promise<MyRank | null> => {
      const data = await apiClient.get<{
        success: boolean;
        rank: number | null;
        qualified_referrals: number;
        in_top_10: boolean;
      }>('/referral/pool/my-rank');
      if (!data?.success) return null;
      return {
        rank: data.rank,
        qualified_referrals: data.qualified_referrals,
        in_top_10: data.in_top_10,
      };
    },
    staleTime: FIVE_MINUTES,
    gcTime: TEN_MINUTES,
    refetchOnWindowFocus: true,
    enabled: !!user,
  });
}

/**
 * Fetch previous month leaderboard (for claiming rewards).
 */
export function useReferralPreviousMonthQuery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: referralKeys.previousMonth(),
    queryFn: async (): Promise<PreviousMonthData | null> => {
      const data = await apiClient.get<{ success: boolean; data: PreviousMonthData | null }>('/referral/pool/previous-month');
      return data?.success ? (data.data ?? null) : null;
    },
    staleTime: FIVE_MINUTES,
    gcTime: TEN_MINUTES,
    enabled: !!user,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

/**
 * Create a new referral invite.
 * Invalidates invites + counts on success.
 */
export function useCreateInviteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      return apiClient.post<{ success: boolean }>('/referral/invites', { email });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: referralKeys.all });
    },
  });
}

/**
 * Send a reminder for a specific invite.
 * Invalidates page-init (invites on page 1) and paginated invites queries.
 */
export function useSendReminderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteId: number) => {
      return apiClient.post<{ success: boolean }>(`/referral/invites/${inviteId}/remind`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: referralKeys.pageInit() });
      queryClient.invalidateQueries({
        queryKey: [...referralKeys.all, 'invites'],
        exact: false,
      });
    },
  });
}

/**
 * Send reminders to all eligible invites.
 * Invalidates page-init (invites on page 1) and paginated invites queries.
 */
export function useRemindAllMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return apiClient.post<{ success: boolean }>('/referral/invites/remind-all');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: referralKeys.pageInit() });
      queryClient.invalidateQueries({
        queryKey: [...referralKeys.all, 'invites'],
        exact: false,
      });
    },
  });
}

/**
 * Claim a tier bonus for a specific referral.
 * Invalidates stats + counts on success.
 */
export function useClaimTierBonusMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ referralId, tierLevel }: { referralId: number; tierLevel: number }) => {
      return apiClient.post<{ success: boolean; message?: string }>(`/referral/referrals/${referralId}/claim-tier/${tierLevel}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: referralKeys.all });
    },
  });
}

/**
 * Claim pool reward from previous month.
 * Invalidates previous-month status and page-init (counts include earned totals).
 */
export function useClaimPoolRewardMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return apiClient.post<{ success: boolean; message?: string }>('/referral/pool/claim');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: referralKeys.previousMonth() });
      queryClient.invalidateQueries({ queryKey: referralKeys.pageInit() });
    },
  });
}

/**
 * Fetch remindable emails (used by remind-all flow).
 * Not a query — called on demand.
 */
export async function fetchRemindableEmails(): Promise<string[]> {
  const data = await apiClient.get<{ success: boolean; emails: string[] }>('/referral/invites/remindable-emails');
  return data?.emails ?? [];
}

/**
 * Force-refresh the pool leaderboard (bypasses cache).
 */
export function useRefreshLeaderboardMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return apiClient.get<{ success: boolean; leaderboard: PoolLeaderboardEntry[] }>('/referral/pool/leaderboard?refresh=true');
    },
    onSuccess: (data) => {
      // Directly set the query data to avoid an extra refetch
      if (data?.leaderboard) {
        queryClient.setQueryData(referralKeys.poolLeaderboard(), data.leaderboard);
      }
    },
  });
}
